<template>
  <v-dialog :value="status" scrollable persistent max-width="850" transition="dialog-transition">
    <v-card>
      <v-card-title>Creating Holiday Hours</v-card-title>
      <v-card-subtitle>Configure how your store operates during holidays</v-card-subtitle>
      <v-card-text>
        <v-card flat>
          <v-card-text class="py-0 px-0">
            <v-row>
              <v-col cols="12" md="12" class="py-0">
                <v-text-field v-model="newHoliday.display_name" label="Holiday Name"></v-text-field>
              </v-col>
              <v-col cols="12" md="12" class="py-0">
                <v-textarea auto-grow rows="1" v-model="newHoliday.message" label="Holiday Message"></v-textarea>
              </v-col>
              <v-col v-if="!isEditing" cols="6" class="py-0">
                <v-menu transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="newHoliday.start_date"
                      label="Start Date"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="newHoliday.start_date"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col v-if="!isEditing" cols="6" class="py-0">
                <v-menu transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field v-model="newHoliday.end_date" label="End Date" readonly v-on="on"></v-text-field>
                  </template>
                  <v-date-picker v-model="newHoliday.end_date"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-divider></v-divider>
        <v-card flat>
          <v-card-title class="px-0">Apply To All</v-card-title>
          <v-card-subtitle
            class="px-0"
          >These hours will be reflected on all the holiday hours listed below</v-card-subtitle>
          <v-card-text class="py-0 px-0">
            <v-row align="center">
              <v-col cols="12" md="4">
                <v-menu
                  ref="openTime_allDays"
                  :close-on-content-click="false"
                  :return-value.sync="allDays.open_time"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field v-model="allDays.open_time" label="Open Time" readonly v-on="on"></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="allDays.open_time"
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      class="text-capitalize"
                      color="primary"
                      @click="$refs.openTime_allDays.save(allDays.open_time)"
                    >Confirm</v-btn>
                  </v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="4">
                <v-menu
                  ref="closeTime_allDays"
                  :close-on-content-click="false"
                  :return-value.sync="allDays.close_time"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="allDays.close_time"
                      label="Close Time"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-time-picker v-model="allDays.close_time">
                    <v-spacer></v-spacer>
                    <v-btn
                      class="text-capitalize"
                      color="primary"
                      @click="$refs.closeTime_allDays.save(allDays.close_time)"
                    >Confirm</v-btn>
                  </v-time-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="2">
                <v-switch inset :label="allDays.is_open?'Open':'Close'" v-model="allDays.is_open"></v-switch>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn @click="applyAll" text color="primary">Apply To All</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-divider></v-divider>
        <v-card flat>
          <v-card-title class="px-0">Hours Of Operation</v-card-title>
          <v-card-subtitle
            class="px-0"
          >Configure hours of operation for your store in the selected date range</v-card-subtitle>
          <v-card-text class="py-0 px-0">
            <v-data-table :headers="headers" :items="newHoliday.holiday_hours">
              <template v-slot:item.date="{ item }">{{item.date || item.holiday_date}}</template>
              <template v-slot:item.open_time="{ item }">
                <v-menu
                  :ref="`openTime${item.holiday_date}`"
                  :close-on-content-click="false"
                  :return-value.sync="item.open_time"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field v-model="item.open_time" label="Open Time" readonly v-on="on"></v-text-field>
                  </template>
                  <v-time-picker v-model="item.open_time">
                    <v-spacer></v-spacer>
                    <v-btn
                      class="text-capitalize"
                      color="primary"
                      @click="$refs[`openTime${item.holiday_date}`].save(item.open_time)"
                    >Confirm</v-btn>
                  </v-time-picker>
                </v-menu>
              </template>
              <template v-slot:item.close_time="{ item }">
                <v-menu
                  :ref="`closeTime_${item.holiday_date}`"
                  :close-on-content-click="false"
                  :return-value.sync="item.close_time"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field v-model="item.close_time" label="Close time" readonly v-on="on"></v-text-field>
                  </template>
                  <v-time-picker v-model="item.close_time">
                    <v-spacer></v-spacer>
                    <v-btn
                      class="text-capitalize"
                      color="primary"
                      @click="$refs[`closeTime_${item.holiday_date}`].save(item.close_time)"
                    >Confirm</v-btn>
                  </v-time-picker>
                </v-menu>
              </template>
              <template v-slot:item.is_open="{ item }">
                <v-row class="justify-center">
                  <v-switch inset :label="item.is_open?'Open':'Close'" v-model="item.is_open"></v-switch>
                </v-row>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="$emit('close-modal')" class="text-capitalize" text color="grey">Cancel</v-btn>
        <v-btn
          v-if="!isEditing"
          @click="addLocationHolidayHours"
          class="text-capitalize"
          color="primary"
        >Confirm</v-btn>
        <v-btn v-else @click="editLocationHolidayHours" class="text-capitalize" color="primary">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { LocationAPI } from '@/clients/locations/locations.api'
export default {
  props: {
    status: {
      default: false
    },
    editHolidayHours: {
      default: () => { return {} }
    }
  },
  data() {
    return {
      newHoliday: {
        id: '',
        display_name: '',
        message: '',
        start_date: '',
        end_date: '',
        holiday_hours: []
      },
      headers: [{
        text: 'Date',
        value: 'date'
      },
      {
        text: 'Open Time',
        value: 'open_time'
      },
      {
        text: 'Close Time',
        value: 'close_time'
      }, {
        text: 'Open',
        value: 'is_open',
        align: 'center'
      }],
      allDays: {
        open_time: '00:00',
        close_time: '00:00',
        is_open: true
      },
      doneInitialEditMapping: false
    }
  },
  methods: {
    applyAll() {
      this.newHoliday.holiday_hours.forEach((hour) => {
        hour.open_time = this.allDays.open_time
        hour.close_time = this.allDays.close_time
        hour.is_open = this.allDays.is_open
      })
    },
    async addLocationHolidayHours() {
      let data = this.newHoliday
      data.holiday_hours.forEach(i => {
        i.open_time = `${i.open_time}:00`
        i.close_time = `${i.close_time}:00`
      })
      let response
      try {
        response = await LocationAPI.addLocationHolidayHours(this.$route.params.id, data)
      } catch (error) {
        return console.log(error)
      }
      this.$emit('holiday-hours-created', response.data.payload)
      this.swal({ type: 'success', title: 'Success', text: 'Holiday Hours Created.' })
    },
    async editLocationHolidayHours() {
      let data = this.newHoliday
      data.location_id = this.$route.params.id
      data.holiday_hours.forEach(i => {
        console.log(i.open_time.length, i.close_time.length)
        if (i.open_time.length < 6) { i.open_time = `${i.open_time}:00` }
        if (i.close_time.length < 6) { i.close_time = `${i.close_time}:00` }
      })
      let response
      try {
        response = await LocationAPI.updateLocationHoliday(data.id, data)
      } catch (error) {
        return console.log(error)
      }
      this.$emit('holiday-hours-updated', response.data.payload)
      this.swal({ type: 'success', title: 'Success', text: 'Holiday Hours Updated.' })
    }
  },
  computed: {
    textFieldValue() {
      if (this.dates.length > 1) {
        return `From: ${this.dates[0]} - To: ${this.dates[1]}`
      } else if (this.dates.length === 1) {
        return `From: ${this.dates[0]} - To: ${this.dates[0]}`
      } else {
        return ''
      }
    },
    watcher_startDateEndDate() {
      return this.newHoliday.start_date + this.newHoliday.end_date
    },
    isEditing() {
      return Object.keys(this.editHolidayHours).length > 0
    }
  },
  watch: {
    watcher_startDateEndDate: {
      handler: function (x) {
        // wait for other handler to see if it has mapped edit fields successfully
        if (this.doneInitialEditMapping) {
          var listDate = []
          if (this.newHoliday.start_date === this.newHoliday.end_date) {
            listDate[0] = this.newHoliday.start_date
          } else {
            var startDate = this.newHoliday.start_date
            var endDate = this.newHoliday.end_date
            var dateMove = new Date(startDate)
            var strDate = startDate
            while (strDate < endDate) {
              strDate = dateMove.toISOString().substr(0, 10)
              listDate.push(strDate)
              dateMove.setDate(dateMove.getDate() + 1)
            };
          }
          this.newHoliday.holiday_hours = []
          for (const index in listDate) {
            let newValue = {
              'holiday_date': listDate[index],
              'open_time': '09:00',
              'close_time': '15:00',
              'is_open': true
            }
            this.$set(this.newHoliday.holiday_hours, index, newValue)
          }
        }
      }
    },
    editHolidayHours: {
      handler: async function (x) {
        if (this.isEditing) {
          Object.keys(this.newHoliday).forEach(key => { this.newHoliday[key] = x[key] })
          // so what happens here is: because holday_hours are also being watched, when i map
          // holiday hours here, their watch function gets triggered and ovverite this hours of the item being edited
          await this.$nextTick()
          this.doneInitialEditMapping = true
        } else {
          this.doneInitialEditMapping = true
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
